import { useIntl } from "react-intl";
import { useState } from "react";
import { Reservation } from "@/components/pages/account/_helpers/useFetchUserReservations";
import { useMountEffect } from "@/hooks/useMountEffect";
import styles from "./UtilsBlock.module.scss";
import {
  AgencyDetails,
  AgencyDetailsResponse,
  sendFetchReservationAgencyDetails,
} from "./useFetchReservationAgencyDetails";

type UtilsBlockProps = {
  reservation: Reservation;
};

const baseMapsUrl = process.env.NEXT_PUBLIC_BASE_MAPS_URL ?? "";

export const UtilsBlock = ({ reservation }: UtilsBlockProps) => {
  const intl = useIntl();

  const [startAgency, setStartAgency] = useState<AgencyDetails>();
  const [endAgency, setEndAgency] = useState<AgencyDetails>();

  const isRoundtrip =
    reservation.travel.startAgency.id === reservation.travel.endAgency.id;

  const getGoogleMapsAgencyLink = (agency: AgencyDetails) =>
    `${baseMapsUrl}${agency.gpsLat},${agency.gpsLong}`;

  const getReservationAgencyDetails = (agencyId: string) => {
    return new Promise<AgencyDetailsResponse>((resolve) => {
      sendFetchReservationAgencyDetails(agencyId)
        .then((json: AgencyDetailsResponse) => {
          resolve(json);
        })
        .catch((e) => {
          console.log(e);
        });
    });
  };

  useMountEffect(() => {
    getReservationAgencyDetails(reservation.travel.startAgency.id.toString())
      .then((response) => {
        setStartAgency(response.data);
        isRoundtrip && setEndAgency(response.data);
      })
      .catch(() => {
        return null;
      });
    if (!isRoundtrip) {
      getReservationAgencyDetails(reservation.travel.endAgency.id.toString())
        .then((response) => {
          setEndAgency(response.data);
        })
        .catch(() => {
          return null;
        });
    }
  });

  return (
    <div className={styles.content}>
      <div className={styles.agencies}>
        <div className={styles.startAgency}>
          <div className={styles.name}>
            {intl.formatMessage({ id: "start" })}
            {reservation.travel.startAgency.name}
          </div>
          {startAgency && (
            <div className={styles.agencyInformationContainer}>
              <div className={styles.address}>{startAgency.address}</div>
              <div className={styles.address}>
                {startAgency.zipCode} {startAgency.city}
              </div>
              <div>
                <a
                  className={styles.link}
                  href={getGoogleMapsAgencyLink(startAgency)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {intl.formatMessage({ id: "itinerary" })}
                </a>
              </div>
              <div className={styles.phone}>{startAgency.telephone}</div>
              <div className={styles.email}>{startAgency.email}</div>
            </div>
          )}
        </div>
        <div className={styles.endAgency}>
          <div className={styles.name}>
            {" "}
            {intl.formatMessage({ id: "end" })}
            {reservation.travel.endAgency.name}
          </div>
          {endAgency && (
            <div>
              <div className={styles.address}>{endAgency.address}</div>
              <div className={styles.address}>
                {endAgency.zipCode} {endAgency.city}
              </div>
              <div>
                <a
                  className={styles.link}
                  href={getGoogleMapsAgencyLink(endAgency)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {intl.formatMessage({ id: "itinerary" })}
                </a>
              </div>
              <div className={styles.phone}>{endAgency.telephone}</div>
              <div className={styles.email}>{endAgency.email}</div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.disclaimer}>
        <div className={styles.title}>
          {intl.formatMessage({ id: "inAgency" })}
        </div>
        <div className={styles.text}>
          {intl.formatMessage({ id: "inAgencyText" })}
          <div className={styles.bold}>
            {intl.formatMessage({ id: "inAgencyTextBold" })}
          </div>
        </div>
      </div>
    </div>
  );
};
